import { CapacityLevel } from './helpers'

// Team Calendar color palette. Two variants - light + dark - read via
// `useTeamCalendarPalette()` which picks based on ThemeContext.
//
// Dark-theme note: capacity purples INVERT visually - a darker purple maps to a
// LOWER share (subtle on dark background), a lighter purple to a HIGHER share
// (pops out more against the dark canvas). The share buckets themselves
// (0/1/2/3/4 → nobody / ≤20 / ≤40 / ≤60 / >60) do not change.

export interface ITeamCalendarPalette {
  capacityBg: Record<CapacityLevel, string | null>
  numberColorForLevel: Record<CapacityLevel, string>
  holidayBg: string
  holidayText: string
  holidayBannerBg: string
  holidayBannerBorder: string
  holidayBannerText: string
  holidayBannerIcon: string
  nonWorkingBg: string
  defaultBg: string
  cellBorder: string
  selectedBorder: string
  selectedTextOnPill: string
  pendingColor: string
  pendingRingBg: string
  panelDivider: string
  panelHeaderText: string
  panelSubText: string
  weekdayLabel: string
  legendDivider: string
}

const DARK_TEXT = 'rgba(0, 0, 0, 0.88)'
const LIGHT_TEXT = 'rgba(255, 255, 255, 0.95)'

export const LIGHT_PALETTE: ITeamCalendarPalette = {
  capacityBg: {
    0: null,
    1: '#c49bf2',
    2: '#b37feb',
    3: '#722ed1',
    4: '#42137f',
  },
  numberColorForLevel: {
    0: DARK_TEXT,
    1: DARK_TEXT,
    2: DARK_TEXT,
    3: LIGHT_TEXT,
    4: LIGHT_TEXT,
  },
  holidayBg: '#fffbe6',
  holidayText: '#000',
  holidayBannerBg: '#fffbe6',
  holidayBannerBorder: '#ffe58f',
  holidayBannerText: '#874d00',
  holidayBannerIcon: '#874d00',
  nonWorkingBg: 'rgba(0, 0, 0, 0.04)',
  defaultBg: '#ffffff',
  cellBorder: '#f0f0f0',
  selectedBorder: '#7f00ff',
  selectedTextOnPill: '#ffffff',
  pendingColor: '#fa8c15',
  pendingRingBg: '#ffffff',
  panelDivider: '#f0f0f0',
  panelHeaderText: 'rgba(0, 0, 0, 0.88)',
  panelSubText: 'rgba(0, 0, 0, 0.45)',
  weekdayLabel: 'rgba(0, 0, 0, 0.45)',
  legendDivider: 'rgba(5, 5, 5, 0.06)',
}

export const DARK_PALETTE: ITeamCalendarPalette = {
  capacityBg: {
    0: null,
    1: 'rgb(131, 68, 214)',
    2: 'rgb(155, 98, 232)',
    3: 'rgb(185, 140, 240)',
    4: 'rgb(220, 196, 250)',
  },
  numberColorForLevel: {
    0: 'rgba(240, 243, 249, 0.88)',
    1: LIGHT_TEXT,
    2: DARK_TEXT,
    3: DARK_TEXT,
    4: DARK_TEXT,
  },
  holidayBg: 'rgb(63, 59, 49)',
  holidayText: 'rgba(240, 243, 249, 0.88)',
  holidayBannerBg: 'rgb(63, 59, 49)',
  holidayBannerBorder: 'transparent',
  holidayBannerText: 'rgba(240, 243, 249, 0.88)',
  holidayBannerIcon: 'rgb(173, 104, 0)',
  nonWorkingBg: 'rgba(240, 243, 249, 0.04)',
  defaultBg: 'rgb(37, 43, 53)',
  cellBorder: 'rgb(73, 88, 98)',
  selectedBorder: 'rgb(209, 163, 255)',
  selectedTextOnPill: 'rgb(37, 43, 53)',
  pendingColor: '#fa8c15',
  pendingRingBg: 'rgb(37, 43, 53)',
  panelDivider: 'rgb(73, 88, 98)',
  panelHeaderText: 'rgba(240, 243, 249, 0.88)',
  panelSubText: 'rgba(240, 243, 249, 0.45)',
  weekdayLabel: 'rgba(240, 243, 249, 0.45)',
  legendDivider: 'rgba(194, 239, 249, 0.23)',
}

export const getTeamCalendarPalette = (isDark: boolean): ITeamCalendarPalette => (isDark ? DARK_PALETTE : LIGHT_PALETTE)
